import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Users, 
  Building2,
  CreditCard,
  TrendingUp,
  Calendar,
  FileText,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { CompanyResource } from '../types';
import { 
  getSubscriptionStatusColor, 
  getSubscriptionStatusLabel,
  getBusinessTypeColor,
  getBusinessTypeLabel
} from '../utils/helpers';

interface CompanyCardProps {
  company: CompanyResource;
}

const CompanyCard: React.FC<CompanyCardProps> = ({ company }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden h-full flex flex-col">
      {/* Company Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center min-w-0 flex-1">
            {company.brandLogo ? (
              <img 
                src={company.brandLogo} 
                alt={company.brandName}
                className="h-12 w-12 rounded-lg object-cover mr-4 flex-shrink-0"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-primary-100 flex items-center justify-center mr-4 flex-shrink-0">
                <Building2 className="h-6 w-6 text-primary-600" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{company.companyName}</h3>
              <p className="text-sm text-gray-500 truncate">{company.brandName}</p>
            </div>
          </div>
          <div className="text-right ml-4 flex-shrink-0">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBusinessTypeColor(company.businessType)}`}>
              {getBusinessTypeLabel(company.businessType)}
            </span>
            <p className="text-sm font-medium text-gray-900 mt-1">{company.companyCode}</p>
          </div>
        </div>
        
        {/* Company Details */}
        <div className="space-y-3">
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="truncate">{company.address || 'No address provided'}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Phone className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
            <span>{company.companyPhone}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Users className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="truncate">{company.companyAdminEmail || 'No admin email'}</span>
          </div>
        </div>

        {/* Status Flags */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-2">
            {/* Need Follow Up Flag */}
            <div className={`flex items-center justify-center px-2 py-1.5 rounded-md text-xs font-medium ${
              company.isNeedFollowUp 
                ? 'bg-red-100 text-red-800' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {company.isNeedFollowUp ? (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Need Follow Up
                </>
              ) : (
                'No Action Needed'
              )}
            </div>

            {/* Phone Number Valid Flag */}
            <div className={`flex items-center justify-center px-2 py-1.5 rounded-md text-xs font-medium ${
              company.isPhoneNumberValid === null
                ? 'bg-gray-100 text-gray-600'
                : company.isPhoneNumberValid 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
            }`}>
              {company.isPhoneNumberValid === null ? (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Not Validated
                </>
              ) : company.isPhoneNumberValid ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Valid Phone
                </>
              ) : (
                <>
                  <XCircle className="h-3 w-3 mr-1" />
                  Invalid Phone
                </>
              )}
            </div>

            {/* Followed Up by Sales Flag */}
            <div className={`flex items-center justify-center px-2 py-1.5 rounded-md text-xs font-medium ${
              company.isFollowedUpBySales 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {company.isFollowedUpBySales ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Followed Up
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Not Contacted
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Info */}
      <div className="p-6 flex-1">
        {company.activeSubscription ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">Subscription</h4>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSubscriptionStatusColor(company.activeSubscription.status)}`}>
                {getSubscriptionStatusLabel(company.activeSubscription.status)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center">
                <CreditCard className="h-3 w-3 mr-1 text-gray-400" />
                <span className="text-gray-600">Level {company.activeSubscription.subscriptionLevel}</span>
              </div>
              <div className="flex items-center">
                <Building2 className="h-3 w-3 mr-1 text-gray-400" />
                <span className="text-gray-600">{company.activeSubscription.maxBranch} branches</span>
              </div>
              <div className="flex items-center">
                <TrendingUp className="h-3 w-3 mr-1 text-gray-400" />
                <span className="text-gray-600">{company.activeSubscription.currentMonthlyPeriodSales.toLocaleString()}/month</span>
              </div>
              <div className="flex items-center">
                <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                <span className="text-gray-600">Valid until {new Date(company.activeSubscription.validUntil || '').toLocaleDateString()}</span>
              </div>
            </div>
            
            {/* Description */}
            {company.activeSubscription.description && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-start">
                  <FileText className="h-3 w-3 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                    {company.activeSubscription.description}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <div className="flex items-center">
              <CreditCard className="h-4 w-4 mr-2 text-yellow-600" />
              <span className="text-sm text-yellow-800">No active subscription</span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-6 pt-0">
        <button
          onClick={() => navigate(`/companies/${company.id}`, { state: { company } })}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          <Eye className="h-4 w-4 mr-2" />
          View Details
        </button>
      </div>
    </div>
  );
};

export default CompanyCard;
